/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string) => ["search", "results", query] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Feed local (stream activity_recent). SIEMPRE con userId: aísla caché entre cuentas. */
    feed: (userId: string | undefined) => ["activity", "feed", userId] as const,
    /** Historial local de una entidad concreta. */
    entity: (userId: string | undefined, entityType: string, entityId: string) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Estado de juego (XP/nivel/racha/badges) derivado del feed de actividad. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
  },
  trash: {
    all: ["trash"] as const,
    products: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "products", userId, orgId] as const,
    warehouses: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "warehouses", userId, orgId] as const,
    locations: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "locations", userId, orgId] as const,
    pickings: (userId: string | undefined, orgId: string | undefined) =>
      ["trash", "pickings", userId, orgId] as const,
  },
  inventory: {
    all: ["inventory"] as const,
    /** Productos de una organización. */
    products: (orgId: string | undefined) => ["inventory", "products", orgId] as const,
    /** Mapa id→nombre de productos (papelera incluida, para el feed). */
    productNames: (orgId: string | undefined) => ["inventory", "productNames", orgId] as const,
    /** Almacenes de una organización. */
    warehouses: (orgId: string | undefined) => ["inventory", "warehouses", orgId] as const,
    /** Ubicaciones de una organización. */
    locations: (orgId: string | undefined) => ["inventory", "locations", orgId] as const,
    /** Quants (on-hand) de una organización. */
    quants: (orgId: string | undefined) => ["inventory", "quants", orgId] as const,
    /** Categorías de producto. */
    categories: (orgId: string | undefined) => ["inventory", "categories", orgId] as const,
    /** Unidades de medida. */
    uoms: (orgId: string | undefined) => ["inventory", "uoms", orgId] as const,
    /** Operaciones (pickings) de una organización. */
    pickings: (orgId: string | undefined) => ["inventory", "pickings", orgId] as const,
    /** Líneas de una operación. */
    pickingLines: (pickingId: string | undefined) =>
      ["inventory", "pickingLines", pickingId] as const,
    /** Reglas de reabastecimiento de una organización. */
    reorderRules: (orgId: string | undefined) => ["inventory", "reorderRules", orgId] as const,
    /** Proveedores de una organización (Stockpilot F3). */
    vendors: (orgId: string | undefined) => ["inventory", "vendors", orgId] as const,
    /** Precios proveedor-producto de una organización. */
    suppliers: (orgId: string | undefined) => ["inventory", "suppliers", orgId] as const,
    /** Órdenes de compra de una organización. */
    purchaseOrders: (orgId: string | undefined) => ["inventory", "purchaseOrders", orgId] as const,
    /** Líneas de una orden de compra. */
    purchaseOrderLines: (poId: string | undefined) =>
      ["inventory", "purchaseOrderLines", poId] as const,
    /** Capas de valoración de una organización. */
    valuationLayers: (orgId: string | undefined) =>
      ["inventory", "valuationLayers", orgId] as const,
    /** Lotes / series de una organización (Stockpilot F4). */
    lots: (orgId: string | undefined) => ["inventory", "lots", orgId] as const,
    /** Lotes de un producto concreto. */
    lotsByProduct: (productId: string | undefined) =>
      ["inventory", "lotsByProduct", productId] as const,
    /** Quants por (producto, ubicación) para FEFO. */
    quantsByLocation: (productId: string | undefined, locationId: string | undefined) =>
      ["inventory", "quantsByLocation", productId, locationId] as const,
    /** Documentos de conteo de una organización. */
    counts: (orgId: string | undefined) => ["inventory", "counts", orgId] as const,
    /** Líneas de un documento de conteo. */
    countLines: (countId: string | undefined) => ["inventory", "countLines", countId] as const,
    /** Listas de materiales (BoM) de una organización (Stockpilot F5). */
    boms: (orgId: string | undefined) => ["inventory", "boms", orgId] as const,
    /** Componentes de una BoM. */
    bomComponents: (bomId: string | undefined) => ["inventory", "bomComponents", bomId] as const,
    /** Órdenes de fabricación de una organización. */
    manufacturingOrders: (orgId: string | undefined) =>
      ["inventory", "manufacturingOrders", orgId] as const,
    /** Landed costs de una organización. */
    landedCosts: (orgId: string | undefined) => ["inventory", "landedCosts", orgId] as const,
    /** Líneas (reparto) de un landed cost. */
    landedCostLines: (landedCostId: string | undefined) =>
      ["inventory", "landedCostLines", landedCostId] as const,
    /** Recepciones (pickings tipo receipt) de una organización. */
    receipts: (orgId: string | undefined) => ["inventory", "receipts", orgId] as const,
    /** Devoluciones a proveedor de una organización. */
    purchaseReturns: (orgId: string | undefined) =>
      ["inventory", "purchaseReturns", orgId] as const,
    /** Líneas de una devolución a proveedor. */
    purchaseReturnLines: (returnId: string | undefined) =>
      ["inventory", "purchaseReturnLines", returnId] as const,
    /**
     * Organizaciones del usuario sincronizadas en LOCAL (PowerSync) para el
     * switcher multi-organización. Clave distinta de `auth.organizations()`
     * (esa es la lista vía API en la página de organizaciones): comparten datos
     * pero no caché, para no pisarse entre la lectura local-first y la de red.
     */
    memberships: () => ["inventory", "memberships", "local"] as const,
  },
  powersync: {
    /** Stats de cola de subida (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** ¿Hay datos locales? (botón de limpieza en profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Catálogo completo de temas (getThemesQuery sin paginar; profile + list-dialog COMPARTEN caché). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  attachments: {
    all: ["attachments"] as const,
    /** Attachments scoped to an owner record (e.g. a product). */
    byOwner: (ownerId: string) => ["attachments", ownerId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
